import React from "react";
import { useSettings } from "../hooks/useSettings";

const SettingsPage: React.FC = () => {
  const { settings, updateSettings, toggleTheme, toggleSound } = useSettings();

  const setExerciseOrder = (order: "biblical" | "random" | "devotional") => {
    updateSettings({ exerciseOrder: order });
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700">
        <div className="p-6 border-b border-zinc-200 dark:border-zinc-700">
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
            Settings
          </h1>
          <p className="text-zinc-600 dark:text-zinc-400 mt-1">
            Customize your learning experience
          </p>
        </div>

        <div className="p-6 space-y-8">
          {/* Appearance Section */}
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
              Appearance
            </h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Theme
                  </label>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    {settings.theme === "dark" ? "Dark mode" : "Light mode"}
                  </p>
                </div>

                <button
                  onClick={toggleTheme}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 ${
                    settings.theme === "dark"
                      ? "bg-cyan-600"
                      : "bg-zinc-200 dark:bg-zinc-600"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.theme === "dark"
                        ? "translate-x-6"
                        : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Audio Section */}
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
              Audio
            </h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Sound Effects (text to speech not affected)
                  </label>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    {settings.soundEnabled ? "On" : "Off"}
                  </p>
                </div>

                <button
                  onClick={toggleSound}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 ${
                    settings.soundEnabled
                      ? "bg-cyan-600"
                      : "bg-zinc-200 dark:bg-zinc-600"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.soundEnabled ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Practice Section */}
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
              Practice
            </h2>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2 block">
                  Exercise Order
                </label>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-3">
                  {settings.exerciseOrder === "random"
                    ? "Exercises appear in random order"
                    : settings.exerciseOrder === "devotional"
                    ? "One exercise per verse in biblical order"
                    : "All exercises in biblical order"}
                </p>
                <div className="flex rounded-lg bg-zinc-100 dark:bg-zinc-700 p-1">
                  <button
                    onClick={() => setExerciseOrder("biblical")}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      settings.exerciseOrder === "biblical"
                        ? "bg-white dark:bg-zinc-600 text-zinc-900 dark:text-zinc-100 shadow-sm"
                        : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200"
                    }`}
                  >
                    Biblical
                  </button>
                  <button
                    onClick={() => setExerciseOrder("devotional")}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      settings.exerciseOrder === "devotional"
                        ? "bg-white dark:bg-zinc-600 text-zinc-900 dark:text-zinc-100 shadow-sm"
                        : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200"
                    }`}
                  >
                    Devotional
                  </button>
                  <button
                    onClick={() => setExerciseOrder("random")}
                    className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      settings.exerciseOrder === "random"
                        ? "bg-white dark:bg-zinc-600 text-zinc-900 dark:text-zinc-100 shadow-sm"
                        : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200"
                    }`}
                  >
                    Random
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
